import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { useQuery } from '@apollo/client';
import BraintreeApplePay from 'braintree-web/apple-pay';
import { useHistory, useParams } from 'react-router-dom';
import { deepRemoveKeys, noop } from '@moda/portal-stanchions';
import { logger } from '../../lib/logger';
import { NULL_USER } from '../../lib/user';
import { reportError } from '../../lib/reportError';
import { getNetworkErrorMessage, isGenericError } from '../../lib/error';
import { PaymentSource } from '../../constants';
import { hrefFor } from '../../routers';
import {
  CartUpdatePropertyName,
  CartUpdateAction,
  CartVersion,
  useUpdateCartMutation,
  GET_CART_QUERY,
  Cart,
  DEFAULT_CART,
  useCart
} from '../../hooks/useCart';
import { usePlaceOrderMutation } from '../../hooks/useCheckout/usePlaceOrderMutation';
import { useChargeOrder } from '../../hooks/useChargeOrder';
import { useErrorModal } from '../../hooks/useErrorModal';
import { useUser } from '../../hooks/useUser';
import { useOrderSummary } from '../../hooks/useOrderSummary';
import { OrderState } from '../../hooks/useUpdateOrder';
import { usePaymentError } from '../../hooks/useCheckout/usePaymentError';
import { useCheckoutErrorTracking } from '../../hooks/useCheckout/useCheckoutErrorTracking';
import { useCookies } from '../../hooks/useCookies';
import { useApplePayBraintreeClient } from './useApplePayBraintreeClient';

const stringToNumStatus = (key: string) =>
  (ApplePaySession as unknown as { [key: string]: number })[key];

type State = {
  cart: Cart;
};

const INITIAL_STATE = {
  cart: DEFAULT_CART
};

type Action = { type: 'UPDATE_CART'; payload: Cart } | { type: 'RESET' };

const ApplePayCartContext = createContext<{
  state: State;
  dispatch: (action: Action) => void;
}>({
  state: INITIAL_STATE,
  dispatch: noop
});

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'UPDATE_CART':
      return {
        ...state,
        cart: action.payload
      };

    case 'RESET':
      return INITIAL_STATE;

    default:
      return state;
  }
};

const ApplePayCartProvider: React.FC<{ children?: React.ReactNode; cart: Cart }> = ({
  children,
  cart
}) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  useEffect(() => {
    if (cart) dispatch({ type: 'UPDATE_CART', payload: cart });
  }, [cart]);

  return (
    <ApplePayCartContext.Provider value={{ state, dispatch }}>
      {children}
    </ApplePayCartContext.Provider>
  );
};

const OnValidateMerchant = ({
  client,
  session,
  onError
}: {
  client: BraintreeApplePay.ApplePay;
  session: ApplePaySession;
  onError?: (error: unknown) => void;
}) => {
  const { showErrorModal } = useErrorModal();

  useEffect(() => {
    session.onvalidatemerchant = event => {
      client
        .performValidation({
          validationURL: event.validationURL,
          displayName: 'Moda Operandi'
        })
        .then(merchantSession => {
          session.completeMerchantValidation(merchantSession);
        })
        .catch(error => {
          reportError('APPLE_PAY_VALIDATE_MERCHANT', error);
          session.abort();
          showErrorModal();
          onError?.(error);
        });
    };
  }, [client, session, showErrorModal, onError]);

  return null;
};

const OnShippingContactSelected = ({
  session,
  onError
}: {
  session: ApplePaySession;
  onError?: (error: unknown) => void;
}) => {
  const { state, dispatch } = useContext(ApplePayCartContext);
  const [updateCart] = useUpdateCartMutation(state.cart);
  const { showErrorModal } = useErrorModal();

  useEffect(() => {
    session.onshippingcontactselected = async event => {
      const result = await updateCart(
        CartUpdateAction.UpdateCart,
        {
          [CartUpdatePropertyName.RenderedBy]: CartVersion.Checkout,
          [CartUpdatePropertyName.PaymentSource]: PaymentSource.Apple,
          address: {
            [CartUpdatePropertyName.State]: event.shippingContact.administrativeArea?.toUpperCase(),
            [CartUpdatePropertyName.City]: event.shippingContact.locality,
            [CartUpdatePropertyName.Phone]: '',
            [CartUpdatePropertyName.PostalCode]: event.shippingContact.postalCode,
            [CartUpdatePropertyName.CountryCode]: event.shippingContact.countryCode?.toUpperCase(),
            [CartUpdatePropertyName.AddressLine1]: '',
            [CartUpdatePropertyName.AddressLine2]: '',
            [CartUpdatePropertyName.FirstName]: '',
            [CartUpdatePropertyName.LastName]: '',
            [CartUpdatePropertyName.Default]: false
          }
        },
        true
      );

      const newCart = result.data?.updateCart.data[0];
      if (newCart) dispatch({ type: 'UPDATE_CART', payload: newCart });
      const newApplePaySummary = deepRemoveKeys(
        newCart?.attributes.applePaymentSummary,
        '__typename'
      );

      if (newApplePaySummary && Number(newApplePaySummary.data.total.amount) > 0) {
        session.completeShippingContactSelection(
          stringToNumStatus(newApplePaySummary.data.status.code),
          newApplePaySummary?.data.shippingMethods,
          newApplePaySummary?.data.total,
          newApplePaySummary?.data.lineItems
        );
      } else {
        showErrorModal();
        session.abort();
        onError?.(new Error('Failed updating shipping contact.'));
      }
    };
  }, [dispatch, session, showErrorModal, state.cart.attributes.currency, updateCart, onError]);

  return null;
};

const OnShippingMethodSelected = ({
  session,
  onError
}: {
  session: ApplePaySession;
  onError?: (error: unknown) => void;
}) => {
  const { showErrorModal } = useErrorModal();
  const { state, dispatch } = useContext(ApplePayCartContext);
  const [updateCart] = useUpdateCartMutation(state.cart);
  useEffect(() => {
    session.onshippingmethodselected = async event => {
      try {
        const contact = state.cart.attributes.applePaymentSummary?.data.shippingContact;

        const result = await updateCart(
          CartUpdateAction.UpdateCart,
          {
            [CartUpdatePropertyName.RenderedBy]: CartVersion.Checkout,
            [CartUpdatePropertyName.PaymentSource]: PaymentSource.Apple,
            address: {
              [CartUpdatePropertyName.State]: contact?.administrativeArea?.toUpperCase(),
              [CartUpdatePropertyName.City]: contact?.locality,
              [CartUpdatePropertyName.Phone]: '',
              [CartUpdatePropertyName.PostalCode]: contact?.postalCode,
              [CartUpdatePropertyName.CountryCode]: contact?.countryCode?.toUpperCase(),
              [CartUpdatePropertyName.AddressLine1]: '',
              [CartUpdatePropertyName.AddressLine2]: '',
              [CartUpdatePropertyName.FirstName]: '',
              [CartUpdatePropertyName.LastName]: '',
              [CartUpdatePropertyName.Default]: false
            },
            [CartUpdatePropertyName.ShippingMethod]: parseInt(
              event.shippingMethod.identifier || '',
              10
            )
          },
          true
        );

        const newCart = result.data?.updateCart.data[0];
        if (newCart) dispatch({ type: 'UPDATE_CART', payload: newCart });
        const newApplePaySummary = deepRemoveKeys(
          newCart?.attributes.applePaymentSummary,
          '__typename'
        );

        if (newApplePaySummary && Number(newApplePaySummary.data.total.amount) > 0) {
          logger.info('APPLE_PAY_SHIPPING_SELECTED', event.shippingMethod.identifier, {
            shippingMethodId: event.shippingMethod.identifier
          });
          session.completeShippingMethodSelection(
            stringToNumStatus(newApplePaySummary.data.status.code),
            newApplePaySummary.data.total,
            newApplePaySummary.data.lineItems
          );
        } else {
          showErrorModal();
          session.abort();
          onError?.(new Error('Failed updating shipping method.'));
        }
      } catch (error) {
        reportError('APPLE_PAY_SHIPPING_SELECTED', error);
        onError?.(error);
      }
    };
  }, [dispatch, session, showErrorModal, state.cart, updateCart, onError]);

  return null;
};

interface OnPaymentAuthorizedProps {
  session: ApplePaySession;
  client: BraintreeApplePay.ApplePay;
  onError?: (error: unknown) => void;
}

const OnPaymentAuthorized: React.FC<OnPaymentAuthorizedProps> = ({ session, client, onError }) => {
  const history = useHistory();
  const [placeOrder] = usePlaceOrderMutation();
  const {
    state: {
      cart: {
        id: cartId,
        attributes: { countryCode, applePaymentSummary, shippingMethods, selectedShippingMethodId }
      }
    }
  } = useContext(ApplePayCartContext);

  const {
    setUserData,
    user: { isLoggedIn, firstName, email }
  } = useUser();
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { orderSummary } = useOrderSummary(orderNumber);
  const { chargeOrder } = useChargeOrder(orderNumber);
  const { setPaymentError } = usePaymentError();
  const { showErrorModal } = useErrorModal();
  const { trackCheckoutError } = useCheckoutErrorTracking();

  useEffect(() => {
    const selectedShippingMethodIdentifier =
      applePaymentSummary?.data.shippingMethods &&
      applePaymentSummary?.data.shippingMethods.find(
        method => `${method.identifier}` === `${selectedShippingMethodId}`
      )?.identifier;
    session.onpaymentauthorized = async event => {
      const braintreePayload = await client
        .tokenize({ token: event.payment.token })
        .catch(error => {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          reportError('APPLE_PAY_BRAINTREE_TOKENIZE', error);
          showErrorModal();
          onError?.(error);
          return 'failed' as const;
        });

      if (braintreePayload === 'failed') return;

      const paymentDetails = {
        card_type: event.payment.token.paymentMethod.network,
        last_four: event.payment.token.paymentMethod.displayName.replace(/[^\d.]/g, ''),
        source: PaymentSource.BraintreeApple
      };

      const billingAddress = {
        billing_address: {
          city: event.payment.billingContact?.locality,
          state: event.payment.billingContact?.administrativeArea?.toUpperCase(),
          postal_code: event.payment.billingContact?.postalCode,
          country_code: event.payment.billingContact?.countryCode?.toUpperCase(),
          address_line1: event.payment.billingContact?.addressLines?.[0],
          address_line2: event.payment.billingContact?.addressLines?.[1],
          first_name: event.payment.billingContact?.givenName,
          last_name: event.payment.billingContact?.familyName
        }
      };

      const shippingAddress = {
        city: event.payment.shippingContact?.locality,
        state: event.payment.shippingContact?.administrativeArea?.toUpperCase(),
        phone: '',
        postal_code: event.payment.shippingContact?.postalCode,
        country_code: event.payment.shippingContact?.countryCode?.toUpperCase(),
        address_line1: event.payment.shippingContact?.addressLines?.[0],
        address_line2: event.payment.shippingContact?.addressLines?.[1],
        first_name:
          event.payment.shippingContact?.givenName ||
          firstName ||
          event.payment.shippingContact?.emailAddress?.split('@')?.[0],
        last_name: event.payment.shippingContact?.familyName,
        email: event.payment.shippingContact?.emailAddress,
        default: false
      };

      const applePayCart = {
        is_guest: !isLoggedIn,
        country_code: countryCode,
        cart_id: cartId || '',
        address: shippingAddress,
        payment_source: PaymentSource.BraintreeApple,
        payment_nonce: braintreePayload.nonce,
        other_payment_details: paymentDetails,
        payment_summary: billingAddress,
        ...(applePaymentSummary?.data.shippingMethods && {
          shipment_method: selectedShippingMethodIdentifier
        })
      };

      const applePaySecondPaymentOrder = {
        id: orderSummary?.id,
        payment_source: PaymentSource.BraintreeApple,
        payment_id: orderSummary?.attributes.selectedPaymentId,
        selected_items_ids: orderSummary?.attributes.selectedItemsIds,
        country_code: countryCode,
        use_site_credit: orderSummary?.attributes.useSiteCredit,
        use_reward_points: orderSummary?.attributes.useMoRewards,
        order_state: OrderState.Charge,
        payment_nonce: braintreePayload.nonce,
        other_payment_details: paymentDetails,
        payment_summary: billingAddress
      };

      if (!orderNumber) {
        try {
          const { data } = await placeOrder(applePayCart);
          const orderData = data?.checkout.data[0].attributes;

          if (orderData && orderData.guestAccessToken) {
            setUserData({
              ...NULL_USER,
              email: orderData.userEmail || event.payment.shippingContact?.emailAddress || '',
              accessToken: orderData.guestAccessToken,
              isMember: orderData.userIsMember || false,
              remember: true
            });
          }

          session.completePayment(ApplePaySession.STATUS_SUCCESS);

          if (orderData)
            history.push(
              hrefFor.OrderConfirmationPage({
                orderNumber: orderData.orderNumber
              })
            );
        } catch (error) {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          if (isGenericError(error) && error.reason !== 'canceled') {
            const networkErrorMessage = getNetworkErrorMessage(error);
            const message =
              networkErrorMessage ||
              error.displayMessage ||
              'An error occurred. Please retry later.';
            reportError('APPLE_PAY_COMPLETE_PAYMENT', error);
            setPaymentError(message ?? null);
            showErrorModal(message);
            logger.info('PAYMENT_METHOD_FAILED', error.toString(), {
              error: error.toString(),
              networkErrorMessage,
              message,
              email: email,
              guest: !isLoggedIn
            });
            trackCheckoutError(PaymentSource.Apple, error);
            onError?.(error);
          }
        }
      } else {
        const response = await chargeOrder(applePaySecondPaymentOrder);

        const orderData = response && response.data?.chargeOrder.data[0].attributes;
        const errorMessage = response && response.data?.chargeOrder.data[0].attributes.message;
        const errors = response && response.data?.chargeOrder.data[0].attributes.errors;
        const hasError =
          response &&
          response.data?.chargeOrder.data[0].attributes.errors &&
          response.data?.chargeOrder.data[0].attributes.errors.length > 0;

        if (errorMessage && hasError) {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          logger.info('SECOND_PAYMENT_FAILED', errorMessage, {
            error: errorMessage,
            email: email,
            guest: !isLoggedIn,
            paymentSource: orderSummary?.attributes.paymentMethod.source,
            payToday: orderSummary?.attributes.paymentSummary.payToday.price,
            total: orderSummary?.attributes.paymentSummary.total.price
          });
          setPaymentError(errorMessage ?? null);
          showErrorModal(errorMessage);
          const error = errors?.[0];
          trackCheckoutError(
            PaymentSource.Apple,
            error && typeof error === 'object' ? error.detail : error
          );
          onError?.(error);
        }

        if (!hasError && orderData) {
          session.completePayment(ApplePaySession.STATUS_SUCCESS);
          logger.info('APPLE_PAY_COMPLETE_PAYMENT_SUCCESS', orderData.userEmail, {
            email: email,
            guest: !isLoggedIn,
            shippingMethod: selectedShippingMethodIdentifier,
            paymentSource: orderSummary?.attributes.paymentMethod.source,
            payToday: orderSummary?.attributes.paymentSummary.payToday.price,
            total: orderSummary?.attributes.paymentSummary.total.price
          });
          history.push(
            hrefFor.OrderDetailPage({
              orderNumber: orderData.orderNumber
            })
          );
        }
      }
    };
  }, [
    applePaymentSummary,
    cartId,
    chargeOrder,
    client,
    countryCode,
    email,
    firstName,
    history,
    isLoggedIn,
    orderNumber,
    orderSummary,
    placeOrder,
    selectedShippingMethodId,
    session,
    setPaymentError,
    setUserData,
    shippingMethods,
    showErrorModal,
    trackCheckoutError,
    onError
  ]);

  return null;
};

type Props = {
  session: ApplePaySession;
  onError?: (error: unknown) => void;
};

export const SetupApplePaySession: React.FC<Props> = ({ session, onError }) => {
  const { orderNumber } = useParams<{ orderNumber: string }>();

  const { client: applePayBraintreeClient } = useApplePayBraintreeClient({ onError });

  const {
    cookies: { cartId }
  } = useCookies();

  const {
    cart: {
      attributes: { country2AlphaCode, countryCode }
    }
  } = useCart();

  const addressBasedCountryCode = country2AlphaCode || countryCode;
  const {
    data: cartData,
    loading: loadingCart,
    error: cartError
  } = useQuery(GET_CART_QUERY, {
    variables: {
      cartId: cartId || '',
      cartVersion: CartVersion.Checkout,
      countryCode: addressBasedCountryCode
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !cartId
  });

  const cart = cartData?.userCart.data[0];

  useEffect(() => {
    if (cart && !loadingCart && applePayBraintreeClient) session.begin();
  }, [cart, loadingCart, applePayBraintreeClient, session]);

  if (cartError) throw cartError;

  if (!cart || loadingCart || !applePayBraintreeClient) return null;

  return (
    <ApplePayCartProvider cart={cart}>
      <OnValidateMerchant client={applePayBraintreeClient} session={session} onError={onError} />

      {!orderNumber && (
        <>
          <OnShippingContactSelected session={session} onError={onError} />
          <OnShippingMethodSelected session={session} onError={onError} />
        </>
      )}

      <OnPaymentAuthorized client={applePayBraintreeClient} session={session} onError={onError} />
    </ApplePayCartProvider>
  );
};
